<script>
  import { onMount } from "svelte";
  import { DataTableSkeleton } from "carbon-components-svelte";
  import Header from "lib/components/Header.svelte";
  import { UPLOAD_ICON } from "@utils/icons.js";
  import { SectionWrapper, Select, Button } from "paxray-components-svelte";
  import SearchNew from "lib/components/searchNew.svelte";
  import SoftwareCatalogTable from "./components/SoftwareCatalogTable.svelte";
  import GenerateSurveyModal from "./components/GenerateSurveyModal.svelte";
  import ImportFrameworkModal from "../damprojects-frontend/components/ImportFrameworkModal.svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { ROUTES } from "@utils/routes.js";
  import { useNavigate } from "svelte-navigator";

  const navigate = useNavigate();

  const headers = [
    { key: "application", value: "Tool" },
    { key: "license_version", value: "Version" },
    { key: "purchased_quantity", value: "Licences" },
    { key: "license_cost_pa", value: "Licence Cost (p.a.)" },
    { key: "service_cost_pa", value: "Service Cost (p.a.)" },
    { key: "users", value: "Users" },
    { key: "license_utilization", value: "Usage" },
    { key: "license_savings_cost", value: "Potential Savings (p.a.)" },
    { key: "renewal_date", value: "Renewal Date" },
    { key: "organization", value: "Groups" },
    { key: "assessment_note", value: "Assessment" },
  ];

  let catalog = [];
  let summary = {};
  let search = "";
  let selectedTeam = "";
  let selectedLicenseStatus = "imported_and_matched";
  let selectedSavings = "";
  let sortKey = "license_savings_cost";
  let sortDirection = "descending";
  let page = 1;
  let pageSize = 25;
  let loading = true;
  let error = "";
  let openImportModal = false;
  let openSurveyModal = false;
  let importSaving = false;
  let preparedSurvey = null;

  const licenseImportFields = [
    {
      key: "vendor",
      label: "Vendor (import only)",
      required: false,
      hidden: true,
      patterns: ["vendor", "anbieter", "lieferant"],
    },
    { key: "name", label: "Product", required: true, patterns: ["product"] },
    {
      key: "applicationname",
      label: "App (Technical Name, matching only)",
      required: false,
      hidden: true,
      patterns: ["technical name", "technical", "app name"],
    },
    {
      key: "license_version",
      label: "License Version",
      required: false,
      patterns: ["license version", "version"],
    },
    {
      key: "license_type",
      label: "License Type",
      required: false,
      patterns: ["license type"],
    },
    {
      key: "subscription_type",
      label: "Subscription Type",
      required: false,
      patterns: ["subscription type", "subscription"],
    },
    {
      key: "purchased_quantity",
      label: "Purchased Licenses",
      required: false,
      patterns: ["# licenses", "licenses", "purchased"],
    },
    {
      key: "comparison_quantity",
      label: "Comparison Quantity",
      required: false,
      patterns: [
        "comparison quantity",
        "lizenzmenge fur vergleich",
        "comparison",
      ],
    },
    {
      key: "license_cost_pa",
      label: "License Cost (p.a.)",
      required: false,
      patterns: ["license cost"],
    },
    {
      key: "service_cost_pa",
      label: "Service Cost (p.a.)",
      required: false,
      patterns: ["service cost"],
    },
    {
      key: "renewal_date",
      label: "Renewal Date",
      required: false,
      patterns: ["renewal date"],
    },
    {
      key: "assessment_note",
      label: "Assessment Note",
      required: false,
      patterns: ["kommentar einordnung", "assessment", "classification"],
    },
  ];

  $: teams = [
    ...new Set(catalog.flatMap((item) => item.teams || []).filter(Boolean)),
  ].sort((a, b) => a.localeCompare(b));
  $: licenseMatchCandidates = catalog.filter(
    (item) => item.license_status === "observed_without_license_data",
  );
  const licenseStatuses = [
    { value: "imported_and_matched", text: "Observed Apps with License Data" },
    {
      value: "imported_matching_needed",
      text: "License Data without App Match",
    },
    {
      value: "observed_without_license_data",
      text: "Observed Apps without License Data",
    },
  ];
  const savingsFilters = [
    { value: "positive", text: "Potential savings identified" },
  ];
  $: filteredApplications = catalog.filter((application) => {
    const query = search.trim().toLowerCase();
    return (
      (!query ||
        [
          application.application,
          application.type,
          application.subscriptiontype,
        ].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(query),
        )) &&
      (!selectedTeam || application.teams?.includes(selectedTeam)) &&
      (!selectedLicenseStatus ||
        application.license_status === selectedLicenseStatus) &&
      (!selectedSavings || Number(application.license_savings_cost) > 0)
    );
  });

  onMount(async () => {
    await loadCatalog();
  });

  async function loadCatalog(resetFilters = true) {
    loading = true;
    error = "";
    if (resetFilters) {
      page = 1;
      selectedTeam = "";
      selectedLicenseStatus = "imported_and_matched";
      selectedSavings = "";
      setDefaultSortForLicenseStatus();
    }
    try {
      const catalogResponse = await fetchWithSession(
        "/api/monitoring/license-catalog",
      );
      const data = await catalogResponse.json();
      if (!catalogResponse.ok) {
        throw new Error(data.message || "License catalog could not be loaded.");
      }
      catalog = (data.applications || []).map((application, index) => ({
        ...application,
        id: `${application.application}-${index}`,
      }));
      summary = data.summary || {};
    } catch (loadError) {
      error = loadError.message || "License catalog could not be loaded.";
      catalog = [];
    } finally {
      loading = false;
    }
  }

  async function handleLicenseUpdate({ licenseId, field, value, row }) {
    const isNewLicense = !licenseId;
    let appMappingId = row.app_mapping_id;
    if (isNewLicense && !appMappingId) {
      const mappingResponse = await fetchWithSession(
        "/api/damprojects/createAppnameMapping",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            applicationname: row.application,
            clearname: row.application,
            apptype: row.type || "unclassified",
          }),
        },
      );
      const mappingData = await mappingResponse.json().catch(() => ({}));
      if (!mappingResponse.ok || !mappingData.id) {
        throw new Error(
          mappingData.message || "A software mapping could not be created.",
        );
      }
      appMappingId = mappingData.id;
      row.app_mapping_id = appMappingId;
    }

    const payload = isNewLicense
      ? {
          app_mapping_id: appMappingId,
          name: row.application,
          [field]: value,
          active: true,
          source: "manual",
        }
      : { [field]: value };

    const response = await fetchWithSession(
      `/api/damprojects/softwareLicenses${isNewLicense ? "" : `/${licenseId}`}`,
      {
        method: isNewLicense ? "POST" : "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(
        data.message || data.error || "The licence value could not be saved.",
      );
    }
    await loadCatalog(false);
  }

  async function handleLicenseMatch({ row, candidate }) {
    const response = await fetchWithSession(
      "/api/damprojects/matchSoftwareLicenses",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          license_ids: (row.licenses || [])
            .map((license) => license.id)
            .filter(Boolean),
          source_app_mapping_id: row.app_mapping_id,
          target_app_mapping_id: candidate.app_mapping_id || null,
          target_application: candidate.application,
          target_type: candidate.type || "unclassified",
        }),
      },
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || "The software could not be matched.");
    }
    await loadCatalog(false);
  }

  async function handleLicenseUnmatch({ row }) {
    const licensePositions = row.licenses || [];
    const manuallyMatchedPositions = licensePositions.filter((license) =>
      String(license.source || "").startsWith("matched:"),
    );
    const licenseIds = (
      manuallyMatchedPositions.length
        ? manuallyMatchedPositions
        : licensePositions
    )
      .map((license) => license.id)
      .filter(Boolean);
    if (!licenseIds.length) {
      throw new Error("No license positions are available for this match.");
    }
    const response = await fetchWithSession(
      "/api/damprojects/unmatchSoftwareLicenses",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ license_ids: licenseIds }),
      },
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(
        data.message || "The software match could not be removed.",
      );
    }
    await loadCatalog(false);
  }

  async function handleLicenseImport(event) {
    importSaving = true;
    error = "";
    try {
      const response = await fetchWithSession(
        "/api/damprojects/importSoftwareLicenses",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ rows: event.detail.rows }),
        },
      );
      const data = await response.json();
      if (!response.ok) {
        const details = data.errors
          ?.slice(0, 3)
          .map((item) => `Row ${item.row}: ${item.message}`)
          .join("; ");
        throw new Error(
          details || data.message || "License import could not be saved.",
        );
      }
      openImportModal = false;
      await loadCatalog();
    } catch (importError) {
      error = importError.message || "License import could not be saved.";
    } finally {
      importSaving = false;
    }
  }

  function resetPage() {
    page = 1;
  }

  function handleLicenseStatusChange() {
    resetPage();
    setDefaultSortForLicenseStatus();
  }

  function setDefaultSortForLicenseStatus() {
    sortKey =
      selectedLicenseStatus === "observed_without_license_data"
        ? "users"
        : "license_savings_cost";
    sortDirection = "descending";
  }

  function handleSurveyPrepared(event) {
    preparedSurvey = event.detail;
  }
</script>

<Header
  section="Digital Twin"
  projectNameOverride="Digital Twin"
  projectHref={ROUTES.dam.root}
  area="Software Intelligence"
  areaClickable={false}
  breadcrumb="License Catalog"
  tooltipText="License Catalog compares purchased licences with observed usage and recorded licence costs."
>
  <Button
    text="Import Licenses"
    kind="secondary"
    size="field"
    iconLeft
    icon={UPLOAD_ICON}
    disabled={importSaving}
    on:click={() => (openImportModal = true)}
  />
  <Button
    text="App Names"
    kind="secondary"
    size="field"
    on:click={() => navigate(ROUTES.dam.appnamemapping)}
  />
  <Button
    text="Generate Survey"
    kind="secondary"
    size="field"
    disabled={loading || catalog.length === 0}
    on:click={() => (openSurveyModal = true)}
  />
</Header>

<ImportFrameworkModal
  bind:open={openImportModal}
  bind:loading={importSaving}
  heading="Import License Data"
  fieldDefinitions={licenseImportFields}
  fileDescription="where the first worksheet contains the App-Overview license data."
  mappingDescription="Map the columns from the first worksheet (App-Overview) to the license fields below."
  on:submit={handleLicenseImport}
/>

<GenerateSurveyModal
  bind:open={openSurveyModal}
  applications={catalog}
  on:submit={handleSurveyPrepared}
/>

<SectionWrapper
  fullscreen
  fitContent
  {loading}
  loadingTitle="Loading licence data."
  loadingHint="Please wait while the License Catalog is prepared."
  emptyState={!loading && !error && catalog.length === 0}
>
  <div slot="emptyState" class="empty_state_text">
    <div class="empty_state_title">
      No data is available for this view with the selected filters.
    </div>
    <div class="empty_state_hint">
      Import licenses or match software to fill the License Catalog.
    </div>
  </div>
  <div class="catalog-main" slot="main">
    <div class="filter-bar">
      <div class="filter-field compact-filter">
        <Select
          placeholder="All teams"
          items={teams.map((team) => ({ value: team, text: team }))}
          bind:selected={selectedTeam}
          onchange={resetPage}
          color="dark"
          labelText="Team"
        />
      </div>
      <div class="filter-field compact-filter">
        <Select
          placeholder="All"
          items={licenseStatuses}
          bind:selected={selectedLicenseStatus}
          onchange={handleLicenseStatusChange}
          color="dark"
          labelText="License status"
        />
      </div>
      <div class="filter-field compact-filter">
        <Select
          placeholder="All tools"
          items={savingsFilters}
          bind:selected={selectedSavings}
          onchange={resetPage}
          color="dark"
          labelText="Savings potential"
        />
      </div>
      <div class="filter-field search-field">
        <SearchNew placeholder="Tool name" bind:searchTerm={search} flexGrow />
      </div>
    </div>

    <section class="catalog-table">
      {#if loading}
        <DataTableSkeleton
          rows={8}
          {headers}
          showToolbar={false}
          showHeader={false}
          size="compact"
        />
      {:else if error}
        <div class="empty-state error">{error}</div>
      {:else if filteredApplications.length === 0}
        <div class="empty-state">No tools match the selected filters.</div>
      {:else}
        <SoftwareCatalogTable
          {headers}
          rows={filteredApplications}
          {summary}
          bind:page
          bind:pageSize
          variant="license"
          bind:sortKey
          bind:sortDirection
          minWidth="136rem"
          onLicenseUpdate={handleLicenseUpdate}
          {licenseMatchCandidates}
          onLicenseMatch={handleLicenseMatch}
          onLicenseUnmatch={handleLicenseUnmatch}
        />
      {/if}
    </section>
  </div>
</SectionWrapper>

<style>
  .empty_state_text {
    text-align: center;
    padding: 3rem 1rem;
  }
  .empty_state_title {
    font-weight: 600;
  }
  .empty_state_hint {
    color: var(--pax-grey-60, #6b7280);
    margin-top: 0.5rem;
  }

  .catalog-main {
    position: relative;
    display: flex;
    width: 100%;
    min-width: 0;
    height: 100%;
    flex-direction: column;
    gap: 1rem;
  }

  .filter-bar {
    display: grid;
    flex: 0 0 auto;
    grid-template-columns: max-content max-content max-content minmax(
        12rem,
        1fr
      );
    gap: 0.65rem;
    align-items: end;
  }

  .filter-field {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.25rem;
  }

  .compact-filter {
    width: max-content;
  }

  .search-field :global(.search) {
    height: 2.5rem;
    padding: 0 0.75rem;
    border-radius: 0.2rem;
  }
  .search-field :global(.searchicon) {
    height: 1.25rem;
  }
  .search-field :global(input) {
    height: 2.25rem;
    padding-left: 0.75rem;
    box-shadow: none !important;
  }
  .search-field :global(input:focus) {
    box-shadow: none !important;
  }

  .catalog-table {
    display: flex;
    min-height: 0;
    flex: 1;
    flex-direction: column;
    overflow: hidden;
    border-radius: 0.5rem;
    background: var(--pax-white);
  }

  .empty-state {
    padding: 2rem;
    color: #525252;
    text-align: center;
  }
  .error {
    color: #a2191f;
  }

  @media (max-width: 52rem) {
    .filter-bar {
      grid-template-columns: 1fr;
    }

    .compact-filter {
      width: max-content;
      max-width: 100%;
    }
  }
</style>
